import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import siteConfig from "../config/siteConfig";
import "../css/contactus.scss";

const initialFormData = {
  firstName: "",
  lastName: "",
  email: "",
  subject: "",
  message: "",
};

function Contact() {
  const [formData, setFormData] = useState(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errors, setErrors] = useState({});
  const submitTimerRef = useRef(null);
  const successTimerRef = useRef(null);

  useEffect(
    () => () => {
      window.clearTimeout(submitTimerRef.current);
      window.clearTimeout(successTimerRef.current);
    },
    []
  );

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));

    if (errors[name]) {
      setErrors((current) => ({ ...current, [name]: "" }));
    }
  };

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const validateForm = () => {
    const nextErrors = {};

    if (!formData.firstName.trim()) nextErrors.firstName = "First name is required.";
    if (!formData.lastName.trim()) nextErrors.lastName = "Last name is required.";

    if (!formData.email.trim()) {
      nextErrors.email = "Email is required.";
    } else if (!isValidEmail(formData.email)) {
      nextErrors.email = "Enter a valid email address.";
    }

    if (!formData.subject) nextErrors.subject = "Choose a subject.";

    if (!formData.message.trim()) {
      nextErrors.message = "Message is required.";
    } else if (formData.message.trim().length < 10) {
      nextErrors.message = "Please share at least 10 characters.";
    }

    return nextErrors;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const formErrors = validateForm();

    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      const firstErrorField = Object.keys(formErrors)[0];
      document.getElementById(firstErrorField)?.focus();
      return;
    }

    setIsSubmitting(true);
    setErrors({});
    window.clearTimeout(submitTimerRef.current);

    submitTimerRef.current = window.setTimeout(() => {
      setShowSuccess(true);
      setFormData(initialFormData);
      setIsSubmitting(false);
      window.scrollTo({ top: 0, behavior: "smooth" });

      window.clearTimeout(successTimerRef.current);
      successTimerRef.current = window.setTimeout(() => {
        setShowSuccess(false);
      }, 6000);
    }, 800);
  };

  const fieldError = (name) =>
    errors[name] ? (
      <p className="contact-form__error" id={`${name}-error`} role="alert">
        {errors[name]}
      </p>
    ) : null;

  return (
    <main className="contact-page" id="main-content">
      <header className="contact-hero">
        <p className="contact-eyebrow">Client care</p>
        <h1>How can we help?</h1>
        <p>
          Questions about an order, a product, or building your planning system?
          Our team is here to make the next step simple.
        </p>
      </header>

      {showSuccess && (
        <div className="contact-success" role="status" aria-live="polite">
          <span aria-hidden="true">&#10003;</span>
          <div>
            <strong>Thank you for reaching out.</strong>
            <p>
              Your note is with the {siteConfig.brandName} team. We will reply
              during our regular service hours.
            </p>
          </div>
          <button type="button" onClick={() => setShowSuccess(false)}>
            <span className="contact-sr-only">Dismiss confirmation</span>
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
      )}

      <div className="contact-layout">
        <aside className="contact-details" aria-label="Contact details">
          <p className="contact-eyebrow">Get in touch</p>
          <h2>We would love to hear from you.</h2>
          <p>
            Send a note using the form, or reach our client care team directly.
            Please include your order number when asking about an existing order.
          </p>

          <dl>
            <div>
              <dt>Email</dt>
              <dd>
                <a href={`mailto:${siteConfig.contact.email}`}>
                  {siteConfig.contact.email}
                </a>
              </dd>
            </div>
            <div>
              <dt>Hours</dt>
              <dd>{siteConfig.contact.hours}</dd>
            </div>
            <div>
              <dt>Business location</dt>
              <dd>{siteConfig.contact.address}</dd>
            </div>
          </dl>

          <nav className="contact-help-links" aria-label="Quick help">
            <h3>Looking for a quick answer?</h3>
            <Link to="/shipping">Shipping information</Link>
            <Link to="/returns">Returns &amp; exchanges</Link>
            <Link to="/privacy">Privacy &amp; data requests</Link>
          </nav>
        </aside>

        <section className="contact-form-panel" aria-labelledby="contact-form-title">
          <div className="contact-form-panel__header">
            <p className="contact-eyebrow">Send a message</p>
            <h2 id="contact-form-title">Tell us what you need.</h2>
            <p>Required fields are marked with an asterisk.</p>
          </div>

          <form className="contact-form" onSubmit={handleSubmit} noValidate>
            <div className="contact-form__row">
              <div className="contact-form__field">
                <label htmlFor="firstName">First name *</label>
                <input
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  autoComplete="given-name"
                  aria-invalid={Boolean(errors.firstName)}
                  aria-describedby={errors.firstName ? "firstName-error" : undefined}
                  required
                />
                {fieldError("firstName")}
              </div>
              <div className="contact-form__field">
                <label htmlFor="lastName">Last name *</label>
                <input
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  autoComplete="family-name"
                  aria-invalid={Boolean(errors.lastName)}
                  aria-describedby={errors.lastName ? "lastName-error" : undefined}
                  required
                />
                {fieldError("lastName")}
              </div>
            </div>

            <div className="contact-form__row contact-form__row--single">
              <div className="contact-form__field">
                <label htmlFor="email">Email address *</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  autoComplete="email"
                  aria-invalid={Boolean(errors.email)}
                  aria-describedby={errors.email ? "email-error" : undefined}
                  required
                />
                {fieldError("email")}
              </div>
            </div>

            <div className="contact-form__field">
              <label htmlFor="subject">What can we help with? *</label>
              <select
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleInputChange}
                aria-invalid={Boolean(errors.subject)}
                aria-describedby={errors.subject ? "subject-error" : undefined}
                required
              >
                <option value="" disabled>
                  Select a subject
                </option>
                <option value="order">Order support</option>
                <option value="product">Product question</option>
                <option value="return">Return or exchange</option>
                <option value="partnership">Press or partnership</option>
                <option value="other">Something else</option>
              </select>
              {fieldError("subject")}
            </div>

            <div className="contact-form__field">
              <label htmlFor="message">Message *</label>
              <textarea
                id="message"
                name="message"
                rows="7"
                value={formData.message}
                onChange={handleInputChange}
                placeholder="Include your order number and any helpful details."
                aria-invalid={Boolean(errors.message)}
                aria-describedby={errors.message ? "message-error" : undefined}
                required
              />
              {fieldError("message")}
            </div>

            <button className="contact-form__submit" type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Sending…" : "Send message"}
            </button>
            <p className="contact-form__privacy">
              By submitting this form, you agree to our <Link to="/privacy">privacy policy</Link>.
            </p>
          </form>
        </section>
      </div>
    </main>
  );
}

export default Contact;
